import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff, X, BookOpen, Target, Megaphone } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const tabs = [
  { id: 'education', label: 'Education Hub', icon: BookOpen },
  { id: 'challenges', label: 'Savings Challenges', icon: Target },
  { id: 'announcements', label: 'Announcements', icon: Megaphone },
];

function ContentForm({ item, onClose, onSave, type }) {
  const [form, setForm] = useState(item || {});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    try {
      let err = null;
      if (type === 'education') {
        const payload = { title: form?.title, category: form?.category || 'tips', body: form?.body, author: form?.author || 'Admin', status: form?.status || 'draft', featured_image_url: form?.featured_image_url || '', tags: form?.tags ? form?.tags?.split(',')?.map(t => t?.trim()) : [] };
        if (item?.id) { ({ error: err } = await supabase?.from('education_content')?.update(payload)?.eq('id', item?.id)); }
        else { ({ error: err } = await supabase?.from('education_content')?.insert(payload)); }
      } else if (type === 'challenges') {
        const payload = { name: form?.name, description: form?.description, duration_days: parseInt(form?.duration_days) || 30, start_date: form?.start_date || null, is_active: form?.is_active !== false };
        if (item?.id) { ({ error: err } = await supabase?.from('savings_challenges')?.update(payload)?.eq('id', item?.id)); }
        else { ({ error: err } = await supabase?.from('savings_challenges')?.insert(payload)); }
      } else if (type === 'announcements') {
        const payload = { message: form?.message, type: form?.type || 'info', is_active: form?.is_active !== false, expiry_date: form?.expiry_date || null };
        if (item?.id) { ({ error: err } = await supabase?.from('announcements')?.update(payload)?.eq('id', item?.id)); }
        else { ({ error: err } = await supabase?.from('announcements')?.insert(payload)); }
      }
      if (err) { setError(err?.message); return; }
      onSave();
      onClose();
    } catch (e) {
      setError('Save failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' };
  const labelStyle = { color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6 overflow-y-auto" style={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)', maxHeight: '90vh' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{item?.id ? 'Edit' : 'Create'} {type === 'education' ? 'Content' : type === 'challenges' ? 'Challenge' : 'Announcement'}</h3>
          <button onClick={onClose} style={{ color: '#64748B' }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {type === 'education' && (
            <>
              <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Title</label><input type="text" value={form?.title || ''} onChange={e => setForm(f => ({ ...f, title: e?.target?.value }))} required className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Category</label>
                  <select value={form?.category || 'tips'} onChange={e => setForm(f => ({ ...f, category: e?.target?.value }))} className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: '#94A3B8' }}>
                    {['tips', 'tutorial', 'strategy', 'module']?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
                  </select>
                </div>
                <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Status</label>
                  <select value={form?.status || 'draft'} onChange={e => setForm(f => ({ ...f, status: e?.target?.value }))} className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: '#94A3B8' }}>
                    <option value="draft">Draft</option><option value="published">Published</option>
                  </select>
                </div>
              </div>
              <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Body</label><textarea value={form?.body || ''} onChange={e => setForm(f => ({ ...f, body: e?.target?.value }))} rows={4} required className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none resize-none" style={inputStyle} /></div>
              <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Tags (comma-separated)</label><input type="text" value={Array.isArray(form?.tags) ? form?.tags?.join(', ') : form?.tags || ''} onChange={e => setForm(f => ({ ...f, tags: e?.target?.value }))} placeholder="budgeting, savings, tips" className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} /></div>
            </>
          )}
          {type === 'challenges' && (
            <>
              <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Challenge Name</label><input type="text" value={form?.name || ''} onChange={e => setForm(f => ({ ...f, name: e?.target?.value }))} required className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} /></div>
              <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Description</label><textarea value={form?.description || ''} onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))} rows={3} required className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none resize-none" style={inputStyle} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Duration (days)</label><input type="number" value={form?.duration_days || 30} onChange={e => setForm(f => ({ ...f, duration_days: e?.target?.value }))} min={1} required className="w-full px-4 py-2.5 rounded-xl text-sm text-white outline-none" style={inputStyle} /></div>
                <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Start Date</label><input type="date" value={form?.start_date || ''} onChange={e => setForm(f => ({ ...f, start_date: e?.target?.value }))} className="w-full px-4 py-2.5 rounded-xl text-sm text-white outline-none" style={{ ...inputStyle, colorScheme: 'dark' }} /></div>
              </div>
            </>
          )}
          {type === 'announcements' && (
            <>
              <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Message</label><textarea value={form?.message || ''} onChange={e => setForm(f => ({ ...f, message: e?.target?.value }))} rows={3} required className="w-full px-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none resize-none" style={inputStyle} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Type</label>
                  <select value={form?.type || 'info'} onChange={e => setForm(f => ({ ...f, type: e?.target?.value }))} className="w-full px-3 py-2.5 rounded-xl text-sm outline-none" style={{ ...inputStyle, color: '#94A3B8' }}>
                    <option value="info">Info</option><option value="warning">Warning</option><option value="promotion">Promotion</option>
                  </select>
                </div>
                <div><label className="block text-xs font-medium mb-1.5" style={labelStyle}>Expiry Date</label><input type="datetime-local" value={form?.expiry_date ? form?.expiry_date?.slice(0, 16) : ''} onChange={e => setForm(f => ({ ...f, expiry_date: e?.target?.value }))} className="w-full px-4 py-2.5 rounded-xl text-sm text-white outline-none" style={{ ...inputStyle, colorScheme: 'dark' }} /></div>
              </div>
            </>
          )}
          {error && <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>}
          <button type="submit" disabled={loading} className="w-full py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
            {loading ? 'Saving...' : item?.id ? 'Update' : 'Create'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ContentManagement() {
  const [activeTab, setActiveTab] = useState('education');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  const tableMap = { education: 'education_content', challenges: 'savings_challenges', announcements: 'announcements' };

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from(tableMap?.[activeTab])?.select('*')?.order('created_at', { ascending: false });
      if (!error) setItems(data || []);
    } catch (err) {
      console.error('Load content error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await supabase?.from(tableMap?.[activeTab])?.delete()?.eq('id', id);
      setActionMsg('Item deleted.');
      loadItems();
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      const field = activeTab === 'education' ? 'status' : 'is_active';
      const newVal = activeTab === 'education' ? (item?.status === 'published' ? 'draft' : 'published') : !item?.is_active;
      await supabase?.from(tableMap?.[activeTab])?.update({ [field]: newVal })?.eq('id', item?.id);
      setActionMsg('Status updated.');
      loadItems();
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      console.error('Toggle error:', err);
    }
  };

  const getStatusLabel = (item) => {
    if (activeTab === 'education') return item?.status;
    if (activeTab === 'challenges') return item?.is_active ? 'active' : 'inactive';
    return item?.is_active ? 'active' : 'inactive';
  };

  const getItemTitle = (item) => item?.title || item?.name || item?.message?.slice(0, 50) + '...' || 'Untitled';
  const getItemSub = (item) => {
    if (activeTab === 'education') return `${item?.category} · ${item?.author || 'Admin'}`;
    if (activeTab === 'challenges') return `${item?.duration_days} days · ${item?.participant_count || 0} participants`;
    return item?.type || 'info';
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Content Management</h2>
        <button onClick={() => { setEditItem(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
          <Plus size={15} /> Create New
        </button>
      </div>

      {actionMsg && (
        <div className="px-4 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}>{actionMsg}</div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {tabs?.map(tab => {
          const Icon = tab?.icon;
          const isActive = activeTab === tab?.id;
          return (
            <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{ background: isActive ? 'linear-gradient(135deg, rgba(0,229,255,0.12), rgba(123,47,190,0.12))' : 'rgba(0,0,0,0.3)', color: isActive ? '#E2E8F0' : '#64748B', border: isActive ? '1px solid rgba(0,229,255,0.2)' : '1px solid rgba(0,229,255,0.08)', fontFamily: 'DM Sans, sans-serif' }}>
              <Icon size={14} /> {tab?.label}
            </button>
          );
        })}
      </div>

      {/* Content List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12 text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Loading content...</div>
        ) : items?.length === 0 ? (
          <div className="text-center py-12 text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No content yet. Create your first item.</div>
        ) : items?.map(item => {
          const status = getStatusLabel(item);
          const isPublished = status === 'published' || status === 'active';
          return (
            <div key={item?.id} className="flex items-center gap-4 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate" style={{ fontFamily: 'DM Sans, sans-serif' }}>{getItemTitle(item)}</p>
                <p className="text-xs mt-0.5 capitalize" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{getItemSub(item)}</p>
              </div>
              <span className="text-xs px-2 py-1 rounded-full flex-shrink-0 capitalize" style={{ background: isPublished ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)', color: isPublished ? '#10B981' : '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}>{status}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button onClick={() => handleToggleStatus(item)} className="p-1.5 rounded-lg" style={{ color: isPublished ? '#F59E0B' : '#10B981', background: 'rgba(0,0,0,0.2)' }} title={isPublished ? 'Unpublish' : 'Publish'}>
                  {isPublished ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button onClick={() => { setEditItem(item); setShowForm(true); }} className="p-1.5 rounded-lg" style={{ color: '#00E5FF', background: 'rgba(0,0,0,0.2)' }}>
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDelete(item?.id)} className="p-1.5 rounded-lg" style={{ color: '#EF4444', background: 'rgba(0,0,0,0.2)' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && <ContentForm item={editItem} onClose={() => { setShowForm(false); setEditItem(null); }} onSave={loadItems} type={activeTab} />}
    </div>
  );
}
